// interfaces
import { OrderData, OrderInterruptionAlertData, InvalidCouponAlertData } from "@swiggy-private/payment-types";
import { GetPaymentMethodsResponse } from "./PaymentApi";
import { AmazonPaySdkContextValue } from "@payment-client/contexts/AmazonPaySdkContext";
import { PlaceOrderData } from "@swiggy-private/checkout-core";

export interface CheckoutActionPayload {
    placeOrderData: PlaceOrderData;
}

interface WebviewRefreshExtraParams {
    webviewRefreshed?: boolean;
    redirectToPaymentPageWhenWebviewRefreshed?: boolean;
    amazonPayTransactionId?: string;
}
export interface ProcessOrderActionPayload extends CheckoutActionPayload {
    order: OrderData;
    useJuspaySDK?: boolean;
    juspayMeta?: GetPaymentMethodsResponse["juspayMeta"];
    isPhonepeSdkAvailable?: boolean;
    apayState?: AmazonPaySdkContextValue["state"];
    startTime?: number;
    webviewRefreshExtraParams?: WebviewRefreshExtraParams;
}

export interface ConfirmOrderActionPayload extends CheckoutActionPayload {
    orderId: string;
    isOrderProcessed?: boolean;
    order: OrderData;
}

export interface OrderInterruptionAlertPayload extends CheckoutActionPayload {
    orderInterruptionAlertData: OrderInterruptionAlertData;
    errorStatusCode: number;
    errorCodes?: string[];
}

export interface InvalidCouponAlertPayload extends CheckoutActionPayload {
    invalidCouponResponseData: InvalidCouponAlertData;
}
export interface VpaTimerActionPayload extends CheckoutActionPayload {
    order: OrderData;
}
export interface UpiIntentPollerActionPayload extends CheckoutActionPayload {
    order: OrderData;
}

export interface RequestCartKeyForPayOnDeliverySuccessPayload {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: string;
}

export interface RequestCartKeyForPayOnDeliveryErrorPayload {
    subTitle: string;
    title: string;
}

export type RequestCartKeyForPayOnDeliveryPayload =
    | RequestCartKeyForPayOnDeliverySuccessPayload
    | RequestCartKeyForPayOnDeliveryErrorPayload;

export enum POST_ORDER_STATUS {
    PROCESSING = "processing",
    PENDING = "pending",
}

export interface PostOrderTickScreenTextHelperBody {
    loading: boolean;
    businessLine: string;
    isPostOrderTip?: boolean;
    isPriorityDelivery?: boolean;
}

export enum UPI_NB_STATUS {
    FAILURE = "FAILURE",
    MWEB_UPI_APP_REDIRECT_FAILURE = "MWEB_UPI_APP_REDIRECT_FAILURE",
}

export enum REVIEW_BILL_DETAILS_WIDGET_TYPE {
    REVIEW_BILL_DETAILS_WIDGET = "REVIEW_BILL_DETAILS_WIDGET",
}

export enum REVIEW_BILL_DETAILS_HALFCARDS {
    HALF_CARD_TYPE_EMI_PLAN_DETAILS = "HALF_CARD_TYPE_EMI_PLAN_DETAILS",
}

export interface BillWidgetLineItem {
    analytics: Analytics;
    items: Array<{
        displayContent: {
            details: Array<{ text: { content: string; dlsColor?: string } }>;
            labelInfo?: { content: string };
        };
    }>;
}

export interface ToPayLineItem {
    displayContent: {
        details: Array<{ text: { content: string; dlsColor?: string } }>;
        subtextDetails?: {
            details: Array<{ text: { content: string } }>;
        };
    };
}

export interface ToPayItem {
    items: ToPayLineItem[];
    analytics: Analytics;
}

export interface BillResponseWidget {
    widgetInfo: {
        widgetType: REVIEW_BILL_DETAILS_WIDGET_TYPE;
        title: string;
        analytics: Record<string, any>;
    };
    data: [
        {
            lineItems: BillWidgetLineItem[];
            toPay: ToPayItem;
        },
    ];
}

export interface ProductDetailsHalfCardInfo {
    emiPlanDetails: Array<{
        emiInterestDetails: string;
        productDetails: Array<{
            finalPrice: string;
            imageId: string;
            name: string;
            quantity: string;
            strikeThroughPrice: string;
        }>;
        title: string;
    }>;
    halfCardType: REVIEW_BILL_DETAILS_HALFCARDS;
    title: string;
}

export interface SubtextDetails {
    details: Array<{
        text: {
            content: string;
            dlsColor: string;
        };
    }>;
    halfCardInfo?: ProductDetailsHalfCardInfo;
}

export interface EMIWidgetLineItemContent {
    displayContent: {
        details: Array<{ text: { content: string; dlsColor?: string } }>;
        subtextDetails?: SubtextDetails;
    };
    strikeThroughContent?: {
        details: Array<{
            text: {
                content: string;
            };
        }>;
    };
}

export interface Analytics {
    context: string;
    impressionObjectName: string;
}

export interface EMIWidgetLineItem {
    analytics: Analytics;
    items: EMIWidgetLineItemContent[];
}

export interface EMIWidgetTotalPayableItem {
    items: Array<{
        displayContent: {
            details: Array<{ text: { content: string } }>;
            subtextDetails?: SubtextDetails;
        };
    }>;
    analytics?: Analytics;
}

export interface EMIResponseWidget {
    widgetInfo: {
        widgetType: REVIEW_BILL_DETAILS_WIDGET_TYPE;
        title: string;
        analytics: Record<string, any>;
    };
    data: Array<{
        bankName: string;
        bankLogoId: string;
        tenureDetails: string;
        emiSubtext: string;
        termsAndConditions: string[];
        lineItems: EMIWidgetLineItem[];
        totalPayable: EMIWidgetTotalPayableItem;
    }>;
}

export interface FetchBillDetailsResponse {
    cardReference: string;
    finalToPay: number;
    widgets: [BillResponseWidget, EMIResponseWidget];
}
